/**
 * Customize home — per-user Admin Home layout.
 *
 * Edits a local draft; nothing changes on the page until Save. Hiding a section
 * only hides it: the data and the pages behind it are unaffected.
 * Pinned sections (Needs your attention) are always on and cannot be hidden.
 */
import { useEffect, useRef, useState, type ReactElement } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronUp } from "lucide-react";

import {
  ADMIN_HOME_ACTION_IDS,
  ADMIN_HOME_ACTION_META,
  ADMIN_HOME_PINNED_SECTION_IDS,
  ADMIN_HOME_PINNED_SECTION_META,
  ADMIN_HOME_SECTION_META,
  DEFAULT_ADMIN_HOME_LAYOUT,
  adminHomeLayoutsEqual,
  hiddenSections,
  hideSection,
  moveSection,
  setActionVisible,
  showSection,
  type AdminHomeLayout,
} from "../adminHomeLayout";
import { saveAdminHomeLayout } from "../useAdminHomeLayout";

import { Button } from "@/components/ui/button/Button";
import { Modal } from "@/components/ui/overlay/Modal";
import { USER_PREFS_QUERY_KEY } from "@/features/preferences/themePreferences";

export type CustomizeAdminHomeDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  /** The layout currently saved (or the default). */
  layout: AdminHomeLayout;
};

const groupTitle =
  "mb-2 text-[11px] font-bold uppercase tracking-wider text-slate-500";
const rowShell =
  "flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2";

export function CustomizeAdminHomeDialog({
  isOpen,
  onClose,
  layout,
}: CustomizeAdminHomeDialogProps): ReactElement {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<AdminHomeLayout>(layout);

  // Start from the saved layout each time the dialog OPENS, not whenever the
  // `layout` prop changes identity: a background refetch hands back an equal
  // copy and resetting then would discard unsaved edits.
  const latestLayout = useRef(layout);
  latestLayout.current = layout;
  useEffect(() => {
    if (isOpen) setDraft(latestLayout.current);
  }, [isOpen]);

  const save = useMutation({
    mutationFn: (next: AdminHomeLayout) =>
      // The default is stored as "nothing saved" so future default changes
      // reach users who never customised.
      saveAdminHomeLayout(
        adminHomeLayoutsEqual(next, DEFAULT_ADMIN_HOME_LAYOUT) ? null : next,
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: USER_PREFS_QUERY_KEY });
      onClose();
    },
  });

  const change = (next: AdminHomeLayout): void => {
    save.reset();
    setDraft(next);
  };

  const hidden = hiddenSections(draft);
  const unchanged = adminHomeLayoutsEqual(draft, layout);
  const quickActionsOn = draft.sections.includes("quick_actions");

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Customize home"
      size="md"
      contentClassName="p-0"
    >
      <div data-testid="customize-home-dialog">
        <p className="px-6 pb-3 text-sm text-slate-500">
          Changes apply to you only.
        </p>

        <div className="max-h-[60vh] space-y-5 overflow-y-auto px-6 pb-4">
          <section aria-labelledby="ch-pinned">
            <h3 id="ch-pinned" className={groupTitle}>
              Always on
            </h3>
            <ul className="space-y-2">
              {ADMIN_HOME_PINNED_SECTION_IDS.map((id) => {
                const meta = ADMIN_HOME_PINNED_SECTION_META[id];
                return (
                  <li
                    key={id}
                    className={rowShell}
                    data-testid={`customize-home-pinned-${id}`}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-slate-900">
                        {meta.label}
                      </span>
                      <span className="block text-xs text-slate-500">
                        {meta.description}
                      </span>
                    </span>
                    <span className="shrink-0 rounded-md bg-slate-100 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                      Always on
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>

          <section aria-labelledby="ch-shown">
            <h3 id="ch-shown" className={groupTitle}>
              On your home page
            </h3>
            {draft.sections.length === 0 ? (
              <p
                className="text-sm text-slate-500"
                data-testid="customize-home-none"
              >
                Nothing else is shown. Add a section below.
              </p>
            ) : (
              <ul className="space-y-2" data-testid="customize-home-shown">
                {draft.sections.map((id, index) => {
                  const meta = ADMIN_HOME_SECTION_META[id];
                  return (
                    <li
                      key={id}
                      className={rowShell}
                      data-testid={`customize-home-row-${id}`}
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium text-slate-900">
                          {meta.label}
                        </span>
                        <span className="block text-xs text-slate-500">
                          {meta.description}
                        </span>
                      </span>
                      <span className="flex">
                        <button
                          type="button"
                          aria-label={`Move ${meta.label} up`} title={`Move ${meta.label} up`}
                          data-testid={`customize-home-up-${id}`}
                          disabled={index === 0}
                          onClick={() => change(moveSection(draft, id, -1))}
                          className="rounded-l-md border border-slate-200 p-1 text-slate-500 hover:bg-slate-50 disabled:cursor-default disabled:opacity-40"
                        >
                          <ChevronUp className="h-4 w-4" aria-hidden />
                        </button>
                        <button
                          type="button"
                          aria-label={`Move ${meta.label} down`} title={`Move ${meta.label} down`}
                          data-testid={`customize-home-down-${id}`}
                          disabled={index === draft.sections.length - 1}
                          onClick={() => change(moveSection(draft, id, 1))}
                          className="-ml-px rounded-r-md border border-slate-200 p-1 text-slate-500 hover:bg-slate-50 disabled:cursor-default disabled:opacity-40"
                        >
                          <ChevronDown className="h-4 w-4" aria-hidden />
                        </button>
                      </span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        aria-label={`Hide ${meta.label}`}
                        data-testid={`customize-home-hide-${id}`}
                        onClick={() => change(hideSection(draft, id))}
                      >
                        Hide
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          {hidden.length > 0 ? (
            <section aria-labelledby="ch-hidden">
              <h3 id="ch-hidden" className={groupTitle}>
                Hidden
              </h3>
              <ul className="space-y-2" data-testid="customize-home-hidden">
                {hidden.map((id) => {
                  const meta = ADMIN_HOME_SECTION_META[id];
                  return (
                    <li
                      key={id}
                      className={rowShell}
                      data-testid={`customize-home-hidden-${id}`}
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium text-slate-900">
                          {meta.label}
                        </span>
                        <span className="block text-xs text-slate-500">
                          {meta.description}
                        </span>
                      </span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        aria-label={`Show ${meta.label}`}
                        data-testid={`customize-home-show-${id}`}
                        onClick={() => change(showSection(draft, id))}
                      >
                        Show
                      </Button>
                    </li>
                  );
                })}
              </ul>
            </section>
          ) : null}

          <section aria-labelledby="ch-actions">
            <h3 id="ch-actions" className={groupTitle}>
              Quick actions
            </h3>
            {quickActionsOn ? null : (
              <p
                className="mb-2 text-xs text-slate-500"
                data-testid="customize-home-actions-note"
              >
                Quick actions are hidden. Show the section to use these.
              </p>
            )}
            <ul className="space-y-2">
              {ADMIN_HOME_ACTION_IDS.map((id) => {
                const meta = ADMIN_HOME_ACTION_META[id];
                const checked = draft.actions.includes(id);
                return (
                  <li key={id} className={rowShell}>
                    <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-3">
                      <input
                        type="checkbox"
                        checked={checked}
                        data-testid={`customize-home-action-${id}`}
                        onChange={(e) =>
                          change(setActionVisible(draft, id, e.target.checked))
                        }
                      />
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-slate-900">
                          {meta.label}
                        </span>
                        <span className="block text-xs text-slate-500">
                          {meta.description}
                        </span>
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </section>
        </div>

        {save.isError ? (
          <p
            role="alert"
            data-testid="customize-home-error"
            className="px-6 pb-2 text-sm text-red-600"
          >
            Couldn’t save your home page. Try again.
          </p>
        ) : null}

        <div className="flex items-center gap-2 border-t border-slate-200 px-6 py-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-testid="customize-home-reset"
            disabled={adminHomeLayoutsEqual(draft, DEFAULT_ADMIN_HOME_LAYOUT)}
            onClick={() => change(DEFAULT_ADMIN_HOME_LAYOUT)}
          >
            Reset to default
          </Button>
          <span className="flex-1" />
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            data-testid="customize-home-save"
            disabled={unchanged || save.isPending}
            onClick={() => save.mutate(draft)}
          >
            {save.isPending ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
